import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from '@/hooks/useInView';
import {
  ArrowRight, ArrowDown, MapPin, Calendar, Globe, Factory,
  Cpu, ShieldCheck, GitBranch, Users, ChevronRight,
  ClipboardCheck, Warehouse, RefreshCw, Wrench, Ruler,
  MessageSquareWarning, FileSearch, GraduationCap, Rocket,
  BarChart3, Zap, UserPlus, Award, Star, CheckCircle,
  ScanBarcode, Monitor, Layers
} from 'lucide-react';
import BookDemoDialog from '@/components/BookDemoDialog';

const RED = '#E02D45';
const BLUE = '#38BDF8';
const GREEN = '#34D399';

function Section({ children, className = '' }) {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} className={`${inView ? 'animate-fade-in-up' : 'opacity-0'} ${className}`}>
      {children}
    </section>
  );
}

function TypewriterLine({ text, visible, startDelay = 0, highlightColor = null }) {
  return (
    <div className="overflow-hidden">
      <span className="inline-block">
        {text.split('').map((char, i) => (
          <span
            key={i}
            className={`inline-block typewriter-char ${visible ? 'typewriter-char-visible' : ''}`}
            style={{ transitionDelay: `${startDelay + i * 60}ms`, color: highlightColor || undefined }}
          >
            {char === ' ' ? '\u00A0' : char}
          </span>
        ))}
      </span>
    </div>
  );
}

const snapshotItems = [
  { value: '2019', label: 'Incorporated', icon: Calendar },
  { value: 'Gurugram', label: 'Headquarters', icon: MapPin },
  { value: 'Pan-India', label: 'Manufacturing Presence', icon: Globe },
  { value: 'Industry 4.0', label: 'Connected Factory Platform', icon: Cpu },
];

const principles = [
  { icon: Factory, title: 'Operational Discipline', desc: 'Every workflow creates structured, auditable evidence. Not just digitized paperwork.' },
  { icon: GitBranch, title: 'Connected Execution', desc: 'Quality, production, traceability, suppliers, and machines in one connected backbone.' },
  { icon: ShieldCheck, title: 'Industrial Reliability', desc: 'Built for the uptime and data integrity that sensitive factory operations demand.' },
  { icon: Cpu, title: 'Shopfloor Practicality', desc: 'Designed by people who have walked factory floors. Every screen works the way plants do.' },
];

const factoryApps = [
  { icon: ClipboardCheck, name: 'Incoming Quality', color: RED },
  { icon: Warehouse, name: 'Digital Stores', color: RED },
  { icon: Factory, name: 'Production Execution', color: RED },
  { icon: ShieldCheck, name: 'Quality Management', color: RED },
  { icon: RefreshCw, name: 'Change Management', color: RED },
  { icon: Wrench, name: 'Maintenance', color: RED },
  { icon: Ruler, name: 'Calibration Lab', color: RED },
  { icon: MessageSquareWarning, name: 'Complaints & CAPA', color: RED },
  { icon: FileSearch, name: 'Audit Management', color: RED },
  { icon: GraduationCap, name: 'Training / DOJO', color: RED },
  { icon: Rocket, name: 'NPD / APQP', color: RED },
  { icon: BarChart3, name: 'Management Dashboards', color: RED },
  { icon: GitBranch, name: 'End-to-End Traceability', color: RED },
  { icon: Cpu, name: 'IoT & Machine Data', color: RED },
  { icon: Zap, name: 'Energy Monitoring', color: RED },
];

const vendorApps = [
  { icon: UserPlus, name: 'Supplier Onboarding', color: BLUE },
  { icon: FileSearch, name: 'Supplier Audits', color: BLUE },
  { icon: Award, name: 'Certificate Management', color: BLUE },
  { icon: MessageSquareWarning, name: 'Supplier Complaints', color: BLUE },
  { icon: CheckCircle, name: 'Corrective Actions', color: BLUE },
  { icon: Star, name: 'Ratings & Scorecards', color: BLUE },
];

const plantLayer = [
  { icon: Users, name: 'Operators' },
  { icon: Cpu, name: 'Machines & PLCs' },
  { icon: ScanBarcode, name: 'Barcodes' },
  { icon: ShieldCheck, name: 'Inspections' },
  { icon: Warehouse, name: 'Material Movement' },
  { icon: Monitor, name: 'Dashboards' },
];

export default function AboutPage() {
  const [demoOpen, setDemoOpen] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 500);
    return () => clearTimeout(t);
  }, [setHeroVisible]);

  return (
    <div data-testid="about-page">
      {/* ═══ 1. HERO ═══ */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-[#060606] overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `linear-gradient(${RED}30 1px, transparent 1px), linear-gradient(90deg, ${RED}30 1px, transparent 1px)`,
          backgroundSize: '80px 80px'
        }} />
        <div className="absolute top-20 right-20 w-[500px] h-[400px] rounded-full blur-[200px]" style={{ backgroundColor: `${RED}05` }} />

        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 relative z-10">
          <div className={`transition-all duration-[1200ms] ease-out ${heroVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '100ms' }}>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: RED }} />
              <p className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: RED }}>About Mushin</p>
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-[4.5rem] font-black uppercase tracking-[-0.03em] text-white leading-[0.92] max-w-5xl" data-testid="about-title">
            <TypewriterLine text="Building the" visible={heroVisible} startDelay={400} />
            <TypewriterLine text="Digital Backbone" visible={heroVisible} startDelay={1300} highlightColor={RED} />
            <TypewriterLine text="of Manufacturing" visible={heroVisible} startDelay={2500} />
          </h1>

          <div className={`transition-all duration-[1400ms] ease-out ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '3800ms' }}>
            <div className="mt-10 h-[3px] w-16" style={{ backgroundColor: RED }} />
            <p className="mt-8 text-lg text-white/45 max-w-2xl leading-[1.7] font-medium">
              <span className="text-white font-bold">MUSHIN</span> digitizes factory operations. Quality, execution, traceability, compliance, supplier governance, and Industry 4.0 workflows for manufacturing plants across India.
            </p>
          </div>
        </div>

        <div className={`absolute bottom-6 left-1/2 -translate-x-1/2 transition-all duration-1000 ${heroVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '4800ms' }}>
          <ArrowDown className="w-5 h-5 text-white/15 animate-bounce" />
        </div>
      </section>

      {/* ═══ 2. COMPANY SNAPSHOT ═══ */}
      <Section className="py-10 lg:py-14 bg-[#0A0A0A] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {snapshotItems.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="text-center py-4" data-testid={`snapshot-${i}`}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-3 border" style={{ backgroundColor: `${RED}10`, borderColor: `${RED}20` }}>
                    <Icon className="w-5 h-5" style={{ color: RED }} strokeWidth={1.7} />
                  </div>
                  <div className="text-2xl sm:text-3xl font-black text-white uppercase tracking-tight">{item.value}</div>
                  <div className="text-[13px] text-white/30 mt-1 font-medium">{item.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ═══ 3. WHO WE ARE + ECOSYSTEM INFOGRAPHIC ═══ */}
      <Section className="py-20 lg:py-28 bg-[#060606] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: RED }}>Who We Are</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
              The Operating System for Factory Operations
            </h2>
            <p className="mt-5 text-base text-white/40 max-w-xl leading-[1.7] font-medium">
              Mushin connects the people, machines, materials, quality records, and supplier data inside manufacturing plants into one operating system.
            </p>
          </div>

          {/* Ecosystem Infographic */}
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6 sm:p-10" data-testid="ecosystem-infographic">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/20 mb-8 text-center">The Mushin Ecosystem</p>

            {/* Vendor Connect Layer */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: BLUE }} />
                <span className="text-[11px] font-bold uppercase tracking-[0.15em]" style={{ color: BLUE }}>Mushin Vendor Connect</span>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {vendorApps.map((app, i) => {
                  const Icon = app.icon;
                  return (
                    <div key={i} className="flex flex-col items-center text-center p-2.5 rounded-lg border bg-white/[0.02]" style={{ borderColor: `${BLUE}12` }}>
                      <Icon className="w-4 h-4 mb-1.5" style={{ color: BLUE }} strokeWidth={1.7} />
                      <span className="text-[9px] font-bold text-white/50 leading-tight">{app.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Connector */}
            <div className="flex justify-center my-3">
              <div className="flex flex-col items-center">
                <div className="w-[2px] h-3" style={{ backgroundColor: `${BLUE}30` }} />
                <ChevronRight className="w-3 h-3 text-white/10 rotate-90" />
                <div className="w-[2px] h-3" style={{ backgroundColor: `${RED}30` }} />
              </div>
            </div>

            {/* Factory OS Layer */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: RED }} />
                <span className="text-[11px] font-bold uppercase tracking-[0.15em]" style={{ color: RED }}>Mushin Factory OS</span>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {factoryApps.map((app, i) => {
                  const Icon = app.icon;
                  return (
                    <div key={i} className="flex flex-col items-center text-center p-2.5 rounded-lg border bg-white/[0.02]" style={{ borderColor: `${RED}12` }}>
                      <Icon className="w-4 h-4 mb-1.5" style={{ color: RED }} strokeWidth={1.7} />
                      <span className="text-[9px] font-bold text-white/50 leading-tight">{app.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Connector */}
            <div className="flex justify-center my-3">
              <div className="flex flex-col items-center">
                <div className="w-[2px] h-3" style={{ backgroundColor: `${RED}30` }} />
                <ChevronRight className="w-3 h-3 text-white/10 rotate-90" />
                <div className="w-[2px] h-3" style={{ backgroundColor: `${GREEN}30` }} />
              </div>
            </div>

            {/* Connected Plant Layer */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: GREEN }} />
                <span className="text-[11px] font-bold uppercase tracking-[0.15em]" style={{ color: GREEN }}>Connected Plant</span>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {plantLayer.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} className="flex flex-col items-center text-center p-2.5 rounded-lg border bg-white/[0.02]" style={{ borderColor: `${GREEN}12` }}>
                      <Icon className="w-4 h-4 mb-1.5" style={{ color: GREEN }} strokeWidth={1.7} />
                      <span className="text-[9px] font-bold text-white/50 leading-tight">{item.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ═══ 4. WHY WE EXIST ═══ */}
      <Section className="py-20 lg:py-28 bg-[#0A0A0A] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: RED }}>Why We Exist</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
              What We Saw Inside Factories
            </h2>
            <p className="mt-8 text-[15px] text-white/40 leading-[1.8] font-medium max-w-2xl">
              Factories still run on paper registers, disconnected spreadsheets, manual approvals, and fragmented visibility. Quality checksheets live in files. Traceability is reconstructed, never live. Audit readiness is a project, not a daily reality.
            </p>
            <p className="mt-5 text-[15px] text-white/55 font-bold max-w-2xl leading-[1.8]">
              Mushin was built to connect execution to evidence, materials to genealogy, and processes to accountability. One operating backbone for the factory.
            </p>
          </div>
        </div>
      </Section>

      {/* ═══ 5. LEADERSHIP ═══ */}
      <Section className="py-20 lg:py-28 bg-[#060606] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: RED }}>Leadership</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
              Product Depth Meets Plant Experience
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Rachit */}
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-7 sm:p-9" data-testid="leader-rachit">
              <div className="flex items-center gap-5 mb-6">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden flex-shrink-0 border-2" style={{ borderColor: `${RED}30` }}>
                  <img src="/rachit.png" alt="Rachit Srivastava" className="w-full h-full object-cover" />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-black text-white uppercase tracking-tight">Rachit Srivastava</h3>
                  <p className="text-sm font-bold mt-1" style={{ color: RED }}>CEO, Managing Director</p>
                </div>
              </div>
              <p className="text-[13px] text-white/40 leading-[1.8] font-medium">
                Leads product, platform, and technology at Mushin. Deep experience in IoT architecture, industrial system design, and manufacturing product development. Built and shipped B2B products for complex, regulation-heavy environments.
              </p>
            </div>

            {/* Amman */}
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-7 sm:p-9" data-testid="leader-amman">
              <div className="flex items-center gap-5 mb-6">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden flex-shrink-0 border-2" style={{ borderColor: `${RED}30` }}>
                  <img src="/amman.png" alt="Amman Batra" className="w-full h-full object-cover" />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-black text-white uppercase tracking-tight">Amman Batra</h3>
                  <p className="text-sm font-bold mt-1" style={{ color: RED }}>COO, Director</p>
                </div>
              </div>
              <p className="text-[13px] text-white/40 leading-[1.8] font-medium">
                Leads operations and customer delivery at Mushin. Over a decade of hands-on experience in Tier-2 automotive manufacturing, plant operations, supplier quality, and shopfloor automation. Shapes how Mushin works in real factory environments.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* ═══ 6. WHAT MUSHIN STANDS FOR ═══ */}
      <Section className="py-20 lg:py-28 bg-[#0A0A0A] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: RED }}>Our Principles</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
              What Mushin Stands For
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {principles.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-6 flex items-start gap-4 hover:border-[#E02D45]/15 transition-all duration-300" data-testid={`principle-${i}`}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border" style={{ backgroundColor: `${RED}10`, borderColor: `${RED}20` }}>
                    <Icon className="w-5 h-5" style={{ color: RED }} strokeWidth={1.7} />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white/80 uppercase">{item.title}</h3>
                    <p className="mt-2 text-[13px] text-white/35 leading-relaxed font-medium">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ═══ 7. BUILT IN INDIA ═══ */}
      <Section className="py-20 lg:py-28 bg-[#060606] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: RED }}>Presence</p>
              <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
                Built in India. Deployed Across India.
              </h2>
              <p className="mt-6 text-[15px] text-white/40 leading-[1.8] font-medium max-w-lg">
                Headquartered in Gurugram. Serving manufacturing plants across India's major industrial belts. Single-plant to multi-plant groups.
              </p>

              <div className="mt-8 bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6" data-testid="presence-card">
                <div className="space-y-4">
                  {[
                    { icon: MapPin, label: 'Headquarters', value: 'Sector 28, DLF Phase 1, Gurugram' },
                    { icon: Globe, label: 'Deployment', value: 'Pan-India manufacturing presence' },
                    { icon: Factory, label: 'Sectors', value: 'Automotive, Pharma, FMCG, Aerospace, Export' },
                    { icon: Users, label: 'Customers', value: 'OEMs, Tier 1, Tier 2, and industrial groups' },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex items-start gap-4">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border" style={{ backgroundColor: `${RED}10`, borderColor: `${RED}20` }}>
                          <Icon className="w-4 h-4" style={{ color: RED }} strokeWidth={1.7} />
                        </div>
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/20">{item.label}</div>
                          <div className="text-sm font-bold text-white/70 mt-0.5">{item.value}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* India Map with City Markers */}
            <div className="relative" data-testid="india-map-section">
              <div className="relative w-full max-w-[500px] mx-auto">
                <img src="/india-map.svg" alt="India Map" className="w-full h-auto opacity-60" />
                {/* City markers - positioned relative to map */}
                {[
                  { name: 'Gurugram', top: '28%', left: '33%', isHQ: true },
                ].map((city, i) => (
                  <div key={i} className="absolute group" style={{ top: city.top, left: city.left, transform: 'translate(-50%, -50%)' }}>
                    <div className={`relative flex items-center justify-center ${city.isHQ ? 'z-20' : 'z-10'}`}>
                      {/* Pulse ring for HQ */}
                      {city.isHQ && (
                        <div className="absolute w-6 h-6 rounded-full animate-ping opacity-30" style={{ backgroundColor: RED }} />
                      )}
                      {/* Dot */}
                      <div className={`rounded-full border ${city.isHQ ? 'w-3.5 h-3.5 border-white/40' : 'w-2 h-2 border-white/20'}`}
                        style={{ backgroundColor: city.isHQ ? RED : `${RED}CC` }} />
                    </div>
                    {/* Label */}
                    <div className={`absolute left-1/2 -translate-x-1/2 whitespace-nowrap pointer-events-none ${city.isHQ ? '-bottom-5' : '-bottom-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200'}`}>
                      <span className={`text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${city.isHQ ? 'text-white bg-[#E02D45]/80' : 'text-white/70 bg-black/60'}`}>
                        {city.name}{city.isHQ ? ' (HQ)' : ''}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ═══ 8. CTA ═══ */}
      <section className="py-28 lg:py-40 relative overflow-hidden bg-[#0A0A0A] border-t border-white/[0.04]" data-testid="about-cta-section">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full blur-[150px]" style={{ backgroundColor: `${RED}06` }} />
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16 text-center relative z-10">
          <p className="text-sm font-bold uppercase tracking-[0.25em] mb-8" style={{ color: RED }}>Get Started</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-[-0.02em] text-white leading-[1]">
            Build Your Factory Backbone<br />with Mushin
          </h2>
          <p className="mt-6 text-white/35 text-lg max-w-lg mx-auto font-medium leading-[1.7]">
            Talk to the team behind <span className="text-white font-bold">MUSHIN</span> about digitizing your plant operations.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="text-white px-10 py-4 rounded-full font-bold text-base inline-flex items-center gap-2.5 transition-all duration-300 hover:shadow-xl" style={{ backgroundColor: RED }} data-testid="about-demo-cta">
              Book a Demo <ArrowRight className="w-5 h-5" />
            </Link>
            <a href="mailto:sales@mushinlabs.com" className="border-2 border-white/15 hover:border-white/30 text-white/45 hover:text-white px-10 py-4 rounded-full font-bold text-base transition-all duration-300" data-testid="about-quote-cta">
              Talk to Sales
            </a>
          </div>
        </div>
      </section>

      <BookDemoDialog open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
}
